/**
 * PD-16801 — what happened to a client visit. Stored on
 * `m_sm_client_visit.status` (varchar, NOT NULL, default 'completed').
 *
 * - `completed` — the visit took place. Every visit logged before this column
 *   existed, and every row the legacy CRM (SM1) inserts without naming the
 *   column, lands here through the database default.
 * - `attempted` — (PD-16957) the rep went to the account but saw no one: it
 *   was closed, or no one was available. The trip there happened, so a sales
 *   trip stop logged this way is visited and the position is judged, but
 *   there was no contact, so it is not counted as a visit.
 * - `planned`   — logged ahead; it is on the calendar and has not happened yet.
 * - `canceled`  — it was planned but did not take place.
 *
 * Only `completed` visits are real visits: they are the only ones counted by
 * the KPI, scorecard, goals, user-activity and visit-log counts, and the only
 * ones that move a client's last-visit and last-contact dates (so an account
 * with only an attempt stays due on the planner and the route guide). Use
 * `CLIENT_VISIT_COUNTED_STATUS` in those queries so the rule lives in one
 * place.
 *
 * SM1 exposure of `attempted` (PD-16957): none. The legacy CRM inserts visit
 * rows without naming `status` and lists every row without reading it, so a
 * new value is invisible to it; what it does read, `client.lastContactDate`,
 * an attempt never moves.
 */
export enum ClientVisitStatus {
	Completed = 'completed',
	Attempted = 'attempted',
	Planned = 'planned',
	Canceled = 'canceled',
}

/** The one status that counts as a visit having happened. */
export const CLIENT_VISIT_COUNTED_STATUS = ClientVisitStatus.Completed;

export const CLIENT_VISIT_STATUS_LABELS: Record<ClientVisitStatus, string> = {
	[ClientVisitStatus.Completed]: 'Completed',
	[ClientVisitStatus.Attempted]: 'Attempted',
	[ClientVisitStatus.Planned]: 'Planned',
	[ClientVisitStatus.Canceled]: 'Canceled',
};

/** Display order for pickers and filters. */
export const CLIENT_VISIT_STATUSES: ReadonlyArray<ClientVisitStatus> = [
	ClientVisitStatus.Completed,
	ClientVisitStatus.Attempted,
	ClientVisitStatus.Planned,
	ClientVisitStatus.Canceled,
];

/** Type guard for a string that may be a visit status. */
export function isClientVisitStatus(
	value: unknown,
): value is ClientVisitStatus {
	return (
		typeof value === 'string' &&
		(CLIENT_VISIT_STATUSES as ReadonlyArray<string>).includes(value)
	);
}

/**
 * PD-16957 — whether the rep was at the account when a visit with this status
 * was logged: completed and attempted visits. Those are the ones whose device
 * position is judged against the account (a planned or canceled visit is
 * logged away from it), and the only ones that may complete a sales trip
 * stop. Wider than "counted": an attempt happened on site but is no visit.
 */
export function clientVisitRecordsPosition(status: ClientVisitStatus): boolean {
	return (
		status === ClientVisitStatus.Completed ||
		status === ClientVisitStatus.Attempted
	);
}
